import React, { useEffect, useMemo, useRef } from "react";
import { LocaleType, createUniver, mergeLocales } from "@univerjs/presets";
import { UniverSheetsCorePreset } from "@univerjs/preset-sheets-core";
import UniverPresetSheetsCoreZhCN from "@univerjs/preset-sheets-core/locales/zh-CN";
import "@univerjs/preset-sheets-core/lib/index.css";

export const MATCH_STATUS = ["matched", "pending_confirm", "unmatched", "invalid"];
export const STATUS_LABELS = {
  matched: "完全匹配",
  pending_confirm: "待确认",
  unmatched: "无法匹配",
  invalid: "异常",
};

export const BOM_SHEET_COLUMNS = [
  { key: "selected", title: "选中", field: "selected", editable: true, width: 66 },
  { key: "originalMpn", title: "原型号", field: "originalMpn", editable: true, width: 190 },
  { key: "originalManufacturer", title: "原品牌", field: "originalManufacturer", editable: true, width: 150 },
  { key: "packageSize", title: "原封装", field: "packageSize", editable: true, width: 150 },
  { key: "originalDescription", title: "原参数", field: "originalDescription", editable: true, width: 220 },
  { key: "originalSupplierPartNo", title: "原C编码", field: "originalSupplierPartNo", editable: true, width: 140 },
  { key: "unitQty", title: "单套用量", field: "unitQty", editable: true, numeric: true, width: 100 },
  { key: "productionSurplusQty", title: "备损量", field: "productionSurplusQty", editable: true, numeric: true, width: 100 },
  { key: "matchStatus", title: "匹配结果", width: 120 },
  { key: "change", title: "更换型号", width: 120 },
  { key: "recommendedMpn", title: "推荐匹配型号", width: 190 },
  { key: "brand", title: "品牌", width: 150 },
  { key: "category", title: "类目", width: 170 },
  { key: "package", title: "封装", width: 120 },
  { key: "params", title: "参数", width: 220 },
  { key: "stock", title: "库存", width: 100 },
  { key: "unitPrice", title: "单价", width: 110 },
  { key: "subtotal", title: "小计", width: 120 },
];

const SHEET_ID = "bom-sheet";
const HEADER_STYLE_ID = "bom-header";
const EDITABLE_STYLE_ID = "bom-editable";
const MATCHED_STYLE_ID = "bom-matched";
const WARNING_STYLE_ID = "bom-warning";
const ERROR_STYLE_ID = "bom-error";

/**
 * 格式化金额显示。
 * @param {number|string} value 原始金额
 * @returns {string} 金额文本
 */
export const formatMoney = (value) => {
  const parsed = Number(value || 0);
  return parsed.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });
};

/**
 * 获取 Univer 表格单元格展示值。
 * @param {Object} item BOM 明细
 * @param {Object} column 列配置
 * @returns {string|number|boolean} 单元格值
 */
export const getBomSheetCellValue = (item, column) => {
  const normalized = item?.normalized || {};
  const product = item?.productSnapshot || {};

  switch (column.key) {
    case "selected":
      return Boolean(item.selected);
    case "matchStatus":
      if (item.matchSource === "ai" && item.requiresConfirmation) return "AI待确认";
      return STATUS_LABELS[item.matchStatus] || item.matchStatus || "-";
    case "change":
      if (item.matchSource === "ai" && item.requiresConfirmation) return "请在上方确认";
      return item.matchStatus === "matched" ? "点击更换" : "待匹配";
    case "recommendedMpn":
      return product.Manufacturer_Part_Number || "-";
    case "brand":
      return product.Manufacturer || "-";
    case "category":
      return product.category || product.sup_category || normalized.commodity || "-";
    case "package":
      return product.product_Attributes?.Package || product.product_Attributes?.["Package / Case"] || normalized.packageSize || "-";
    case "params":
      return product.description || normalized.normalizedDescription || normalized.originalDescription || "-";
    case "stock":
      return product.stock || "-";
    case "unitPrice":
      return `$${formatMoney(item.unitPrice)}`;
    case "subtotal":
      return `$${formatMoney(item.subtotal)}`;
    default:
      return column.field ? normalized[column.field] ?? "" : "";
  }
};

/**
 * 根据匹配状态返回行样式。
 * @param {string} status 匹配状态
 * @returns {string} Univer 样式 ID
 */
const getRowStyleId = (status) => {
  if (status === "matched") return MATCHED_STYLE_ID;
  if (status === "pending_confirm") return WARNING_STYLE_ID;
  return ERROR_STYLE_ID;
};

/**
 * 获取 Univer 单元格值类型。
 * @param {unknown} value 单元格值
 * @returns {number} Univer 单元格类型
 */
const getCellType = (value) => {
  if (typeof value === "number") return 2;
  if (typeof value === "boolean") return 4;
  return 1;
};

/**
 * 构建 Univer 工作簿快照。
 * @param {Object} bomOrder BOM 任务
 * @param {Object[]} items 当前展示的 BOM 明细
 * @param {Object[]} columns 当前显示且已排序的列
 * @returns {Object} Univer 工作簿数据
 */
export const buildBomWorkbookSnapshot = (bomOrder, items = [], columns = BOM_SHEET_COLUMNS) => {
  const cellData = {
    0: columns.reduce((row, column, columnIndex) => {
      row[columnIndex] = {
        v: column.title,
        t: 1,
        s: HEADER_STYLE_ID,
      };
      return row;
    }, {}),
  };

  items.forEach((item, itemIndex) => {
    const rowIndex = itemIndex + 1;
    const rowStyleId = getRowStyleId(item.matchStatus);
    cellData[rowIndex] = columns.reduce((row, column, columnIndex) => {
      const value = getBomSheetCellValue(item, column);
      row[columnIndex] = {
        v: value,
        t: getCellType(value),
        s: column.editable ? EDITABLE_STYLE_ID : rowStyleId,
      };
      return row;
    }, {});
  });

  return {
    id: `bom-workbook-${bomOrder?._id || "draft"}`,
    name: bomOrder?.originalFileName || "BOM Online Sheet",
    appVersion: "3.0.0-alpha",
    locale: LocaleType.ZH_CN,
    styles: {
      [HEADER_STYLE_ID]: {
        bg: { rgb: "#fff4c2" },
        bl: 1,
        ff: "Arial",
        fs: 11,
        ht: 2,
        vt: 2,
        tb: 3,
      },
      [EDITABLE_STYLE_ID]: {
        bg: { rgb: "#fffdf3" },
        bd: { b: { s: 1, cl: { rgb: "#d9e2ec" } } },
        ff: "Arial",
        fs: 11,
        vt: 2,
      },
      [MATCHED_STYLE_ID]: {
        bg: { rgb: "#f2fbff" },
        ff: "Arial",
        fs: 11,
        vt: 2,
      },
      [WARNING_STYLE_ID]: {
        bg: { rgb: "#fffaf0" },
        ff: "Arial",
        fs: 11,
        vt: 2,
      },
      [ERROR_STYLE_ID]: {
        bg: { rgb: "#fff5f5" },
        ff: "Arial",
        fs: 11,
        vt: 2,
      },
    },
    sheetOrder: [SHEET_ID],
    sheets: {
      [SHEET_ID]: {
        id: SHEET_ID,
        name: "BOM",
        rowCount: Math.max(items.length + 30, 80),
        columnCount: columns.length,
        freeze: {
          xSplit: 0,
          ySplit: 1,
          startRow: 1,
          startColumn: 0,
        },
        defaultColumnWidth: 110,
        defaultRowHeight: 34,
        columnData: columns.reduce((columnData, column, index) => {
          columnData[index] = { w: column.width };
          return columnData;
        }, {}),
        rowData: {
          0: { h: 36 },
        },
        cellData,
        showGridlines: 1,
        rowHeader: { width: 46 },
        columnHeader: { height: 30 },
        mergeData: [],
      },
    },
  };
};

/**
 * 将 Univer 工作簿里的可编辑列转换为 BOM 明细补丁。
 * @param {Object} univerAPI Univer facade API
 * @param {Object[]} items 当前展示的 BOM 明细
 * @param {Object[]} columns 当前显示且已排序的列
 * @returns {Object[]} BOM 明细补丁列表
 */
export const extractBomSheetRows = (univerAPI, items = [], columns = BOM_SHEET_COLUMNS) => {
  const workbook = univerAPI?.getActiveWorkbook?.();
  const sheet = workbook?.getActiveSheet?.();
  if (!sheet || items.length === 0) return [];

  const values = sheet.getRange(1, 0, items.length, columns.length).getValues();
  return items.map((item, rowIndex) => {
    const rowValues = values[rowIndex] || [];
    const normalized = {};
    let selected = item.selected;

    columns.forEach((column, columnIndex) => {
      if (!column.editable || !column.field) return;
      const value = rowValues[columnIndex];

      if (column.key === "selected") {
        selected = value === true || value === "TRUE" || value === "true" || value === "是" || value === "1" || value === 1;
        return;
      }

      normalized[column.field] = column.numeric ? Number(value || 0) : `${value ?? ""}`.trim();
    });

    return {
      itemId: item._id,
      selected,
      normalized,
    };
  });
};

/**
 * 根据 Univer 当前选区取得对应的 BOM 明细 ID。
 * 表头和工作簿预留的空白行会被忽略。
 * @param {Object} univerAPI Univer facade API
 * @param {Object[]} items 当前展示的 BOM 明细
 * @returns {string[]} 选中明细 ID 列表
 */
export const getSelectedBomItemIds = (univerAPI, items = []) => {
  const workbook = univerAPI?.getActiveWorkbook?.();
  const sheet = workbook?.getActiveSheet?.();
  if (!sheet || items.length === 0) return [];

  const selection = sheet.getSelection?.();
  const activeRanges = selection?.getActiveRangeList?.()
    || [sheet.getActiveRange?.()].filter(Boolean);
  const selectedIds = new Set();

  activeRanges.forEach((activeRange) => {
    const range = activeRange?.getRange?.();
    if (!range) return;

    const startRow = Math.max(1, Number(range.startRow || 0));
    const endRow = Math.min(items.length, Number(range.endRow ?? range.startRow ?? 0));
    for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
      const itemId = items[rowIndex - 1]?._id;
      if (itemId) selectedIds.add(itemId);
    }
  });

  return Array.from(selectedIds);
};

/**
 * 渲染 BOM 的 Univer Sheets 在线表格。
 * @param {Object} props 组件属性
 * @param {Object} props.bomOrder BOM 任务
 * @param {Object[]} props.items 当前展示的 BOM 明细
 * @param {Object[]} props.columns 当前显示且已排序的列
 * @param {(api: Object|null) => void} props.onReady Univer API 就绪回调
 * @returns {JSX.Element} Univer 容器
 */
const UniverBomSheet = ({ bomOrder, items, columns = BOM_SHEET_COLUMNS, onReady }) => {
  const containerRef = useRef(null);
  const univerRef = useRef(null);
  const workbookSnapshot = useMemo(
    () => buildBomWorkbookSnapshot(bomOrder, items, columns),
    [bomOrder, columns, items],
  );

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const { univerAPI } = createUniver({
      locale: LocaleType.ZH_CN,
      locales: {
        [LocaleType.ZH_CN]: mergeLocales(UniverPresetSheetsCoreZhCN),
      },
      presets: [
        UniverSheetsCorePreset({
          container: containerRef.current,
        }),
      ],
    });

    univerAPI.createWorkbook(workbookSnapshot);
    univerRef.current = univerAPI;
    onReady?.(univerAPI);

    return () => {
      onReady?.(null);
      univerAPI.dispose();
      univerRef.current = null;
    };
  }, [onReady, workbookSnapshot]);

  return <div ref={containerRef} className="bom-univer-container" />;
};

export default UniverBomSheet;
